import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaFolderOpen } from '@react-icons/all-files/fa/FaFolderOpen';
import { FaProjectDiagram } from '@react-icons/all-files/fa/FaProjectDiagram';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import { FaPlus } from '@react-icons/all-files/fa/FaPlus';
import Breadcrumb from '../../../../commons/Breadcrumb';
import PageHeader from '../../../../commons/PageHeader';
import PageAlert, { usePageAlert } from '../../../../commons/PageAlert';
import Confirm, { useConfirm } from '../../../../commons/Confirm';
import { ConfigButton, EditButton, DeleteButton, RenderActions } from '../../../../commons/DataGridActions';
import ProfaseDataGrid from '../../../../commons/Datagrid';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { getEntities, deleteEntity, reset } from '../../../../manager/categoria-actividad.reducer';
import {
  APLICAR_POLITICA_FILTER_OPTIONS,
  CATEGORIA_ACTIVIDAD_BASE,
  categoriaActividadChildState,
  categoriaActividadConfigUrl,
  formatDesplazamiento,
  getAjusteDiaNoHabilLabel,
  getPoliticaTipoLabel,
} from '../../../../shared/categoria-actividad';
import { TIPO_POLITICA_OPTIONS } from '../../../../shared/politica-vencimiento';

const BASE = CATEGORIA_ACTIVIDAD_BASE;

function AplicarPoliticaBadge({ value }: { value?: boolean | null }) {
  if (value == null) {
    return <span style={{ color: 'var(--crm-text-muted)', fontSize: 12 }}>Sin política</span>;
  }
  return (
    <span style={{
      fontSize: 12,
      fontWeight: 600,
      color: value ? '#15803d' : 'var(--crm-text-muted)',
    }}
    >
      {value ? 'Sí' : 'No'}
    </span>
  );
}

export default function CategoriaActividadPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const topRef = useRef<HTMLDivElement>(null);

  const entities = useAppSelector(s => s.categoriaActividad.entities);
  const totalItems = useAppSelector(s => s.categoriaActividad.totalItems);
  const loading = useAppSelector(s => s.categoriaActividad.loading);
  const updating = useAppSelector(s => s.categoriaActividad.updating);
  const updateSuccess = useAppSelector(s => s.categoriaActividad.updateSuccess);

  const [rows, setRows] = useState<any[]>([]);
  const [lastParams, setLastParams] = useState<any>(null);

  const { alert, showToast } = usePageAlert();
  const { confirmConfig, ask, dismiss, setConfirmLoading } = useConfirm();

  useEffect(() => {
    const msg = (location.state as any)?.successMsg;
    if (msg) { showToast('success', msg); window.history.replaceState({}, ''); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (updateSuccess) {
      dispatch(reset());
      dismiss();
      if (lastParams) dispatch(getEntities(lastParams));
      showToast('success', 'Categoría de actividad eliminada correctamente.');
    }
  }, [updateSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setConfirmLoading(updating); }, [updating]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setRows(entities ?? []); }, [entities]);

  const fetchData = (params: any) => {
    setLastParams(params);
    dispatch(getEntities(params));
  };

  const listSearch = location.search;

  const renderActions = (params: any) => (
    <RenderActions>
      <ConfigButton
        title="Configuración (mail y vencimiento)"
        onClick={() => navigate(categoriaActividadConfigUrl(params.row.id), {
          state: categoriaActividadChildState(listSearch),
        })}
      />
      <EditButton onClick={() => navigate(`${BASE}/${params.row.id}/edit`, {
        state: categoriaActividadChildState(listSearch),
      })} />
      <DeleteButton onClick={() => {
        ask(
          <>¿Confirmas que deseas eliminar <strong>{params.row.descripcion ?? `#${params.row.id}`}</strong>? Esta acción no se puede deshacer.</>,
          () => dispatch(deleteEntity(params.row.id)),
          { title: 'Eliminar Categoría de Actividad', confirmLabel: 'Eliminar', variant: 'danger' },
        );
        topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }} />
    </RenderActions>
  );

  const cols = [
    { field: 'id', headerName: 'ID', flex: 0.4 },
    { field: 'descripcion', headerName: 'Descripción', flex: 1.6 },
    {
      field: 'tipoActividad.descripcion',
      headerName: 'Tipo actividad',
      flex: 1,
      sortable: false,
      renderCell: (p: any) => p.row.tipoActividad?.descripcion ?? '—',
    },
    {
      field: 'aplicar',
      headerName: 'Aplica',
      flex: 0.7,
      sortable: false,
      renderCell: (p: any) => <AplicarPoliticaBadge value={p.row.aplicar} />,
    },
    {
      field: 'politicaDescripcion',
      headerName: 'Política',
      flex: 1.2,
      renderCell: (p: any) => p.row.nombre ?? '—',
    },
    {
      field: 'politicaTipoRegla',
      headerName: 'Tipo regla',
      flex: 1.3,
      renderCell: (p: any) => getPoliticaTipoLabel(p.row.tipo),
    },
    {
      field: 'politicaDesplazamiento',
      headerName: 'Desplaz.',
      flex: 0.6,
      renderCell: (p: any) => formatDesplazamiento(p.row.desplazamiento, p.row.tipo),
    },
    {
      field: 'politicaAjusteInhabil',
      headerName: 'Ajuste inhábil',
      flex: 1,
      renderCell: (p: any) => getAjusteDiaNoHabilLabel(p.row.ajusteDiaNoHabil),
    },
    { field: '__actions', headerName: '', flex: 1.1, sortable: false, renderCell: renderActions },
  ];

  const filters = [
    { label: 'Descripción', name: 'descripcion' },
    {
      label: 'Aplicar política',
      name: 'aplicar',
      type: 'select',
      single: true,
      options: [...APLICAR_POLITICA_FILTER_OPTIONS],
    },
    {
      label: 'Tipo de regla',
      name: 'tipo',
      type: 'select',
      options: [...TIPO_POLITICA_OPTIONS],
    },
  ];

  return (
    <div style={g.page} ref={topRef}>
      <Breadcrumb items={[
        { label: 'Inicio', to: '/', icon: <FaHome /> },
        { label: 'Configuración', to: '/configuracion', icon: <FaCog /> },
        { label: 'Workflow', to: '/configuracion/workflow', icon: <FaProjectDiagram /> },
        { label: 'Categoría de Actividad', icon: <FaFolderOpen /> },
      ]} />

      <PageHeader
        icon={<FaFolderOpen />}
        iconColor="#f59e0b"
        title="Categoría de Actividad"
        subtitle="Categorías de actividad y políticas de vencimiento"
        action={{
          label: <><FaPlus style={{ marginRight: 6 }} />Nueva Categoría</>,
          onClick: () => navigate(`${BASE}/new`, { state: categoriaActividadChildState(listSearch) }),
          color: '#f59e0b',
        }}
      />

      <Confirm config={confirmConfig} />
      {alert && <PageAlert alert={alert} />}

      <ProfaseDataGrid
        cols={cols}
        title=""
        filters={filters}
        fetch={fetchData}
        rowCount={totalItems}
        loading={loading}
        rows={rows}
        defaultSortField="descripcion"
      />
    </div>
  );
}

const g: Record<string, React.CSSProperties> = {
  page: { display: 'flex', flexDirection: 'column', gap: 24 },
};
